import { writable } from 'svelte/store';
import type { Address } from './address';
import type { Election } from './elections';
import type { SubscriptionsResponse } from './subscriptions';
import { clearToasts } from '../lib/Toast.svelte';

export interface VvSettings {
    api: {
        url: string;
        key: string;
    },
    associationIdName: string,
    siteNumber: number,
    keyValueProfile?: string,
    address?: Address,
    electionCenter?: {
        address?: Address;
        associationIdName: string;
        urlRegisterToVote: string;
        urlFindYourPollingLocation: string;
        urlGetPersonalizedVotingResources: string;
        urlViewOfficials: string;
        urlViewOfficialsWithAddress: string;
        urlMyCandidates: string;
        elections: Election[];

        customSectionHeader?: string;
        customSectionBody?: string;
        imageUrl?: string;
    },
    subscriptions?: SubscriptionsResponse,
    directory: {
        hiddenDisplayCategories: string[]
    };
    showBlogs?: boolean;
}

export interface ValidationMessage {
    propertyName: string
    message: string
}
export const vvSettings = (window as any).vvSettings as VvSettings;
/** The base url for api calls (typically '/Api'). */
const base = vvSettings?.api?.url || '/Api';
/** The usersite api key (you can copy it from usersite while testing). */
const key = vvSettings?.api?.key;
const headers = {
    "Content-Type": "application/json; charset=utf-8",
    "Authorization": key,
    // TODO - add language?
};
export const associationIdName = vvSettings?.associationIdName ?? vvSettings?.electionCenter?.associationIdName;
export const isLoading = writable(false);
export const siteNumber = vvSettings?.siteNumber ? vvSettings.siteNumber : 0;

export interface ApplicationSettings {
    userProfile: UserProfileSettings
}

export interface UserProfileSettings {
    homeAddressRequired : boolean,
    homeAddressVisible : boolean,
    businessAddressRequired : boolean,
    businessAddressVisible : boolean,
    preferredAddressType: string
}

const applicationSettingsCache: Record<string, ApplicationSettings> = {};
export const getApplicationSettings = async (applicationType?: string) => {
    applicationType = applicationType ?? '';
    if (!applicationSettingsCache[applicationType]) {
        let response = undefined;
        if (applicationType !== '') {
            try {
                const url = `ApplicationSettings?association=${associationIdName}&applicationType=${applicationType ?? ''}`;
                response = await fetchGet(url) as ApplicationSettings;
            } catch (error) {
                response = undefined;
            }
        }
        if (!response) {
            response = { userProfile: { homeAddressRequired: false, businessAddressRequired: false, preferredAddressType: 'H' }}
        }
        applicationSettingsCache[applicationType] = response;
    }

    return applicationSettingsCache[applicationType];
}

export const getDatePart = (dateString: string | undefined, part: 'day' | 'month' | 'year'): number => {
    const dateParts = (dateString != undefined && dateString != null && dateString.trim() != "") ? dateString.split('/') : [];

    if (dateParts.length !== 3) {
        throw new Error('Invalid date format. Please use "mm/dd/yyyy" format.');
    }

    const [month, day, year] = dateParts.map(Number);
    switch (part) {
        case 'day':
            return day;
        case 'month':
            return month;
        case 'year':
            return year;
        default:
            throw new Error('Invalid date part. Use "day", "month", or "year".');
    }
}

export const getLocalizedMonthAbbreviation = (dateString: string | undefined): string => {
    const formatter = new Intl.DateTimeFormat(navigator.language, { month: 'short' });
    return formatter.format(new Date(dateString ?? ``));
}

export class ApiResponseError extends Error {
    statusCode: number;
    data?: any;
    
    constructor(statusCode: number, message: string, data?: any) {
        super();
        this.message = message;
        this.data = data;
        this.statusCode = statusCode;
    }
}

export const callApi = async <TBody, TResponse>(url: string, method: string, body?: TBody, hideLoadingBehavior?: boolean, ignoreBasePath?: boolean): Promise<TResponse> => {
    if (!hideLoadingBehavior) hideLoadingBehavior = false;
    try {
        if (hideLoadingBehavior === false)
            isLoading.set(true);
        clearToasts('failure');
        const urlWithBase = `${ignoreBasePath ? '' : base}/${url}`;
        const r = await fetch(urlWithBase, {
            headers,
            method,
            body: body ? JSON.stringify(body) : undefined,
        });
        const contentType = r.headers.get("content-type");
        const isJson = contentType?.includes("application/json") ?? false;
        const json: TResponse = isJson ? await r.json() : await r.text();
        if (r.ok) {
            return json;
        } else {
            if (typeof json === 'string' || json instanceof String) {
                throw new ApiResponseError(r.status, `${json}`);
            } 
                
            throw new ApiResponseError(r.status, JSON.stringify(json), json);
        }
    } catch (error) {
        // Re-throw the error so that it can be handled by the caller
        throw error;
    } finally {
        if (hideLoadingBehavior === false)
            isLoading.set(false);
    }
}

export const fetchGet = async <T>(url: string, hideLoadingBehavior?: boolean) => await callApi<undefined, T>(url, "GET", undefined, hideLoadingBehavior);
export const fetchPost = async <TBody, TResponse>(url: string, body: TBody, ignoreBasePath?: boolean) => await callApi<TBody, TResponse>(url, "POST", body, false, ignoreBasePath);
export const fetchPut = async <TBody, TResponse>(url: string, body: TBody) => await callApi<TBody, TResponse>(url, "PUT", body);
export const fetchDelete = async <TBody, TResponse>(url: string, body: TBody, ignoreBasePath?: boolean) => await callApi<TBody, TResponse>(url, "DELETE", body, false, ignoreBasePath);


export const noPictureUrl: string = 'https://www.votervoice.net/photos/nopicture.jpg';
export const scrollIntoView = (el: Element) => {
    if (window.innerWidth >= 1280 || !el || el == null) return;
    el.scrollIntoView({
        behavior: 'smooth'
    });
}

export const extractDistinctStrings = (objects: any[]) => {
    return [...new Set(objects.flatMap(obj =>
        Object.values(obj).filter(value => typeof value === 'string')
    ))];
};

export const levelsOfTargetsStrings = {
    federal: 'federal',
    state: 'state',
    local: 'local',
    media: 'media'
}
export const countryStrings = {
    us: 'us',
    ca: 'ca'
}

export function convertToQueryStringParams(json: any) {
    return Object.keys(json)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(json[key]))
        .join('&');
}

export function getCleanHashFragment() {
    const hash = window.location.hash.substring(1) || '';
    return hash.split('?')[0];
}

export function getQueryParam(paramName: string) {
    const hash = window.location.hash.substring(1);
    const queryIndex = hash.indexOf('?');
    if (queryIndex === -1) return null;

    const queryString = hash.substring(queryIndex + 1);
    const params = new URLSearchParams(queryString);
    return params.get(paramName);
}